import { toApiError, type PaginationMeta } from "@/shared/api/common";
import { http } from "@/shared/api/http";
import type { OrganizationRegion, OrganizationType } from "@/shared/config/constants";
import type { PromoDetails } from "@/shared/ui/promo-badge";

export type OrganizationMetric = "exclusive" | "cheapest" | "promotion";
export type OrganizationMetricSortBy =
  "matched_days" | "matched_offers" | "last_matched_date" | "matched_price" | "medicine_name" | "price_gap_pct";
export type OrganizationMetricSortDir = "asc" | "desc";

export type OrganizationMetricMedicine = {
  medicine_id: string;
  raw_name?: string | null;
  medicine?: {
    id?: string;
    name?: string | null;
    trade_name?: string | null;
    international_name?: string | null;
    dosage_form?: string | null;
    strength?: string | null;
    volume?: string | null;
    pack?: string | number | null;
  } | null;
  matched_days?: number | null;
  matched_offers?: number | null;
  last_matched_date?: string | null;
  matched_price?: number | null;
  rival_price?: number | null;
  competitor_count?: number | null;
  latest?: {
    issue_date?: string | null;
    price?: number | null;
    markup?: number | null;
    expiry_date?: string | null;
    vendor_id?: string | null;
    vendor_name?: string | null;
    package_quantity?: number | null;
    region?: string | null;
  } | null;
  promotion?: {
    promo_price?: number | null;
    regular_price?: number | null;
    promotion_text?: string | null;
    promotion_type?: string | null;
    buy_quantity?: number | null;
    bonus_quantity?: number | null;
    bonus_medicine_raw_name?: string | null;
    promo_start_date?: string | null;
    promo_end_date?: string | null;
  } | null;
};

export type OrganizationMetricDrilldownResponse = {
  organization?: {
    organization_id?: string;
    name?: string | null;
    slug?: string | null;
    city?: string | null;
    region?: OrganizationRegion | null;
    logo_sm?: string | null;
    type?: OrganizationType | null;
  };
  summary?: {
    metric?: OrganizationMetric;
    unit?: "medicines" | "medicine_days" | "offers";
    metric_total?: number;
    matched_medicines?: number;
    window_days?: number;
    payment_percentage?: number;
    region?: string | null;
    truncated?: boolean;
    promotion_source_truncated?: boolean;
  };
  data: OrganizationMetricMedicine[];
  pagination?: PaginationMeta;
};

export async function fetchOrganizationMetricMedicines(params: {
  organizationId: string;
  metric: OrganizationMetric;
  region?: OrganizationRegion;
  payment_percentage?: number;
  search?: string;
  sort_by?: OrganizationMetricSortBy;
  sort_dir?: OrganizationMetricSortDir;
  page?: number;
  limit?: number;
}): Promise<OrganizationMetricDrilldownResponse> {
  try {
    const response = await http.get<OrganizationMetricDrilldownResponse>(
      `/analytics/organization-rankings/${params.organizationId}/medicines`,
      {
        params: {
          metric: params.metric,
          region: params.region,
          payment_percentage: params.payment_percentage,
          q: typeof params.search === "string" ? params.search.trim() || undefined : undefined,
          sort_by: params.sort_by,
          sort_dir: params.sort_dir,
          page: params.page,
          limit: params.limit,
        },
      },
    );
    return response.data;
  } catch (error) {
    throw toApiError(error, "Unable to load ranking medicines.");
  }
}

export function toPromoDetails(row: OrganizationMetricMedicine): PromoDetails {
  const promotion = row.promotion;
  return {
    price: promotion?.regular_price ?? row.latest?.price ?? null,
    promo_price: promotion?.promo_price ?? null,
    promotion_text: promotion?.promotion_text ?? null,
    promotion_type: promotion?.promotion_type ?? null,
    buy_quantity: promotion?.buy_quantity ?? null,
    bonus_quantity: promotion?.bonus_quantity ?? null,
    bonus_medicine_raw_name: promotion?.bonus_medicine_raw_name ?? null,
    promo_start_date: promotion?.promo_start_date ?? null,
    promo_end_date: promotion?.promo_end_date ?? null,
  };
}
